import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  BarChart3,
  Building2,
  CreditCard,
  Settings,
  FileText,
  LogOut,
  TrendingUp,
  User,
  ChevronDown,
  Mail,
  BookOpen,
  MessageSquare,
  Menu,
  X,
  Crown
} from 'lucide-react';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: #f8f9fa;
`;

const Sidebar = styled.aside`
  width: 260px;
  background: #0a1220;
  color: #e2e8f0;
  display: flex;
  flex-direction: column;
  padding: 24px 20px;
  border-right: 1px solid rgba(148, 163, 184, 0.1);

  @media (max-width: 1024px) {
    width: 220px;
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.4rem;
  font-weight: 700;
  color: #00d4aa;
`;

const AdminBadge = styled.span`
  margin-top: 8px;
  align-self: flex-start;
  background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
  color: white;
  padding: 0.2rem 0.7rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const NavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.2s ease;

  ${props => props.$active ? `
    background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
    color: #0a1220;
  ` : `
    color: #cbd5f5;
    &:hover {
      background: rgba(148, 163, 184, 0.12);
      color: #f8fafc;
    }
  `}
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TopBar = styled.header`
  background: white;
  padding: 1rem 2rem;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 860px) {
    gap: 0.75rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: white;
  color: #0a1220;
  font-weight: 600;
  cursor: pointer;
  flex: 0 0 auto;

  &:hover {
    border-color: #00d4aa;
  }

  @media (max-width: 860px) {
    display: inline-flex;
  }
`;

const MobileNavOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(10, 18, 32, 0.55);
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 1200;
`;

const MobileNav = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: min(84vw, 320px);
  background: #0a1220;
  color: #e2e8f0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transform: ${props => props.$open ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.25s ease;
  z-index: 1300;
`;

const MobileNavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const MobileCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #e2e8f0;
  border: none;
  padding: 0.35rem;
  cursor: pointer;
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.9rem;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f8f9fa;
    border-color: #00d4aa;
  }

  @media (max-width: 860px) {
    padding: 0.5rem 0.7rem;
    gap: 0.5rem;
    max-width: 62vw;
  }
`;

const UserInfo = styled.div`
  text-align: left;

  @media (max-width: 860px) {
    max-width: 40vw;
  }
`;

const UserName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  color: #666;
  font-size: 0.75rem;

  @media (max-width: 860px) {
    display: none;
  }
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: #666;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1000;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1rem;
  text-align: left;
  color: #333;
  font-weight: 500;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 8px 8px;
  }

  &:hover {
    background: #f8f9fa;
  }

  &.logout {
    color: #dc3545;

    &:hover {
      background: #fff5f5;
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Main = styled.main`
  max-width: none;
  margin: 0;
  padding: 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout } = useAdminAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={16} />, path: '/admin/dashboard' },
    { key: 'companies', label: 'Empresas', icon: <Building2 size={16} />, path: '/admin/companies' },
    { key: 'plans', label: 'Planos', icon: <FileText size={16} />, path: '/admin/plans' },
    { key: 'financial', label: 'Financeiro', icon: <CreditCard size={16} />, path: '/admin/financial' },
    { key: 'payment-settings', label: 'Pagamentos', icon: <CreditCard size={16} />, path: '/admin/payment-settings' },
    { key: 'crm', label: 'CRM', icon: <Mail size={16} />, path: '/admin/crm' },
    { key: 'loyalty', label: 'Fidelização', icon: <Crown size={16} />, path: '/admin/loyalty' },
    { key: 'blog', label: 'Blog', icon: <BookOpen size={16} />, path: '/admin/blog' },
    { key: 'site-content', label: 'Conteúdo do Site', icon: <FileText size={16} />, path: '/admin/site-content' },
    { key: 'contact', label: 'Contato', icon: <MessageSquare size={16} />, path: '/admin/contato' },
    { key: 'seo', label: 'SEO', icon: <TrendingUp size={16} />, path: '/admin/seo' },
    { key: 'settings', label: 'Configurações', icon: <Settings size={16} />, path: '/admin/settings' }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/app/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const getCurrentNavKey = () => {
    const path = location.pathname;
    const item = navItems.find(item => item.path === path);
    return item?.key || 'dashboard';
  };

  return (
    <LayoutContainer>
      <MobileNavOverlay
        $open={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen(false)}
      />
      <MobileNav $open={isMobileMenuOpen}>
        <MobileNavHeader>
          <SidebarHeader>
            <User size={26} />
            PontoFiel
          </SidebarHeader>
          <MobileCloseButton onClick={() => setIsMobileMenuOpen(false)}>
            <X size={22} />
          </MobileCloseButton>
        </MobileNavHeader>
        <AdminBadge>ADMIN</AdminBadge>
        <Nav>
          {navItems.map((item) => (
            <NavItem
              key={item.key}
              $active={getCurrentNavKey() === item.key}
              onClick={() => {
                navigate(item.path);
                setIsMobileMenuOpen(false);
              }}
            >
              {item.icon}
              {item.label}
            </NavItem>
          ))}
        </Nav>
      </MobileNav>

      <Sidebar>
        <SidebarHeader>
          <User size={26} />
          PontoFiel
        </SidebarHeader>
        <AdminBadge>ADMIN</AdminBadge>
        <Nav>
          {navItems.map((item) => (
            <NavItem
              key={item.key}
              $active={getCurrentNavKey() === item.key}
              onClick={() => navigate(item.path)}
            >
              {item.icon}
              {item.label}
            </NavItem>
          ))}
        </Nav>
      </Sidebar>

      <Content>
        <TopBar>
          <TopBarContent>
            <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
              <Menu size={18} />
              Menu
            </MobileMenuButton>
            <UserMenuContainer ref={dropdownRef}>
              <UserMenuButton onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                <UserInfo>
                  <UserName>{admin?.name || 'Administrador'}</UserName>
                  <UserRole>{admin?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</UserRole>
                </UserInfo>
                <ChevronIcon $isOpen={isDropdownOpen}>
                  <ChevronDown size={20} />
                </ChevronIcon>
              </UserMenuButton>

              <DropdownMenu $isOpen={isDropdownOpen}>
                <DropdownItem onClick={() => {
                  setIsDropdownOpen(false);
                  navigate('/admin/profile');
                }}>
                  <User size={18} />
                  Editar Perfil
                </DropdownItem>
                <DropdownItem className="logout" onClick={() => {
                  setIsDropdownOpen(false);
                  handleLogout();
                }}>
                  <LogOut size={18} />
                  Sair
                </DropdownItem>
              </DropdownMenu>
            </UserMenuContainer>
          </TopBarContent>
        </TopBar>

        <Main>
          {children}
        </Main>
      </Content>
    </LayoutContainer>
  );
};
